import qs from 'querystring'
import { createContext, useContext, useEffect, useState } from 'react'

import * as amplitudeAnalytics from '@amplitude/analytics-browser'
import {
  Experiment,
  ExperimentClient,
  ExperimentConfig,
  Source
} from '@amplitude/experiment-js-client'

export const AmplitudeContext = createContext<{
  experiment: ExperimentClient | null
  analytics: amplitudeAnalytics.Types.BrowserClient | null
}>({
  experiment: null,
  analytics: null
})

export const useAmplitude = () => {
  const context = useContext(AmplitudeContext)

  if (!context) {
    throw new Error('useAmplitude must be used within an AmplitudeProvider')
  }

  return context
}

const AmplitudeProvider = ({ children }: { children: React.ReactNode }) => {
  const [experiment, setExperiment] = useState<ExperimentClient | null>(null)

  useEffect(() => {
    const experimentConfig: ExperimentConfig = {
      debug:
        process.env.NEXT_PUBLIC_ENVIRONMENT_NAME === 'production'
          ? false
          : true,
      automaticExposureTracking: true,
      automaticFetchOnAmplitudeIdentityChange: true,
      retryFetchOnFailure: true,
      pollOnStart: false,
      source: Source.LocalStorage
    }

    const amplitudeExperimentsApiKey = `${process.env
      .NEXT_PUBLIC_AMPLITUDE_EXPERIMENTS_API_KEY!}`

    const experiment = Experiment.initializeWithAmplitudeAnalytics(
      amplitudeExperimentsApiKey,
      experimentConfig
    )

    const queryParams = qs.parse(window.location.search.slice(1))

    if (queryParams['exp_variant']) {
      amplitudeAnalytics.setDeviceId(queryParams['exp_variant'] as string)
    }

    ;(async () => {
      await experiment
        .fetch({ user_properties: queryParams as Record<string, string> })
        .then((experimentClient) => {
          setExperiment(experimentClient)
        })

      if (typeof window === 'object') {
        window.amplitude = amplitudeAnalytics
      }
    })()
  }, [])

  return (
    <AmplitudeContext.Provider
      value={{ experiment, analytics: amplitudeAnalytics }}
    >
      {children}
    </AmplitudeContext.Provider>
  )
}

export default AmplitudeProvider
